import {
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { TimeStamped } from '@shared/models/timestamped';
import { DateTime } from 'luxon';
import {
  Observable,
  Subject,
  catchError,
  map,
  of,
  share,
  startWith,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { SignalsService } from '../signals/signals.service';
import { MessageService } from 'primeng/api';
import { ErrorLogService } from '../errorhandler/error-log.service';
import { AdminService } from 'app/features/admin/admin.service';

@Injectable({
  providedIn: 'root',
})
export class ApiCacheService {
  private readonly CACHE_EXPIRATION_MINUTES = 30; // Time before refresh.
  private readonly MAX_CACHE_AGE_DAYS = 7; // Time before deletion.

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private signalService: SignalsService,
    private messageService: MessageService,
    private errorLogService: ErrorLogService,
    private adminService: AdminService
  ) { }

  /**
   * Returns a cached response immediately if one exists. If the cached response
   * has expired, a network request will also be initiated. If a postBody is provided,
   * POST will be used instead of GET.
   * @template T Response type
   * @param {string}   cacheId    The unique string used to identify this response.
   * @param {string}   url        The request url.
   * @param {any}      headers    The url options header that includes token & subscription key 
   * @param {unknown}  postBody  Body to submit with POST request.
   * @returns {Observable<RequestState<T>>}
   */
  request<T extends TimeStamped>(
    cacheId: string,
    url: string,
    dynamicKey: string | undefined,
    postBody?: unknown,
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(cacheId);
    if (cachedResponse) {
      if (
        cachedResponse.timestamp &&
        DateTime.fromISO(cachedResponse.timestamp, { zone: 'utc' }).diffNow(
          'minutes'
        ).minutes *
        -1 <=
        this.CACHE_EXPIRATION_MINUTES
      ) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse
          )
        );
      }
    }

    // console.log(`Cached data expired. Refreshing ${cacheId}.`);
    // No/expired cache, make network request.
    if (postBody) {
      return this.forceRefreshPost<T>(cacheId, url, dynamicKey, postBody);
    } else {
      return this.forceRefreshGet<T>(cacheId, url, dynamicKey);
    }
  }

  /**
   * Disregards cache expiration and triggers API call. Still immediately returns cached value
   * while loading. Also returns cached value on failure.
   */
  forceRefreshGet<T extends TimeStamped>(
    cacheId: string,
    url: string,
    dynamicKey?: string
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(cacheId);
    return this.http.get<T>(url).pipe(
      map((response) => {
        if (response) {
          this.cacheResponse(cacheId, response, dynamicKey);
        }
        if (dynamicKey) {
          const cachedResponse = this.getCached<T>(cacheId);
          return new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse
          );
        }

        return new RequestState<T>(
          RequestStatus.IsCompleteWithResponse,
          response
        );
      }),
      catchError((err) => this.handleError(err, cachedResponse)),
      startWith(
        new RequestState<T>(
          RequestStatus.IsLoadingWithCachedResponse,
          cachedResponse
        )
      ),
      tap((response) => {
        this.signalService.isLoading$.set(response.isLoading);
      }),
      takeUntil(this.destroy$),
      share()
    );
  }

  /**
   * POST version of forceRefreshGet.
   */
  forceRefreshPost<T extends TimeStamped>(
    cacheId: string,
    url: string,
    dynamicKey: string | undefined,
    postBody?: unknown
  ): Observable<RequestState<T>> {
    const cachedResponse = this.getCached<T>(cacheId);
    return this.http.post<T>(url, postBody).pipe(
      map((response) => {

        if (response) {
          this.cacheResponse(cacheId, response, dynamicKey);
        }

        if (dynamicKey) {
          const cachedResponse = this.getCached<T>(cacheId);
          return new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse
          );
        }

        return new RequestState<T>(
          RequestStatus.IsCompleteWithResponse,
          response
        );
      }),
      catchError((err) => this.handleError(err, cachedResponse)),
      startWith(
        new RequestState<T>(
          RequestStatus.IsLoadingWithCachedResponse,
          cachedResponse
        )
      ),
      tap((response) => {
        this.signalService.isLoading$.set(response.isLoading);
      }),
      share()
    );
  }

  /** Clear local storage items that are older than the specified age. */
  clearOldCache(): void {
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      const item = localStorage.getItem(key);
      if (item) {
        try {
          const parsedItem: TimeStamped = JSON.parse(item);
          if (parsedItem.timestamp) {
            const age =
              DateTime.fromISO(parsedItem.timestamp, { zone: 'utc' }).diffNow(
                'days'
              ).days * -1;

            if (age > this.MAX_CACHE_AGE_DAYS) {
              // console.log(`Removing ${key}: ${age.toFixed(0)}.`);
              localStorage.removeItem(key);
            }
          }
        } catch {
          continue;
        }
      }
    }
  }

  /**
   * Gets the cached response if found. Returns null if not found.
   * @template T Response type.
   * @param {string} cacheId Tnique string to identify response.
   * @returns {T | null}
   */
  private getCached<T>(cacheId: string): T | null {
    const cached = localStorage.getItem(cacheId);
    
    if (!cached) {
      return null;
    }

    return JSON.parse(cached) as T;
  }

  /** Saves response to cache. */
  private cacheResponse<T extends TimeStamped>(
    cacheId: string,
    response: T,
    dynamicKey?: string
  ): void {
    if (cacheId !== '') {
      if (Array.isArray(response)) {
        let key;
        if (dynamicKey) {
          key = dynamicKey;
        } else {
          key = "response";
        }
        const cacheObject = {
          [key]: response,
          timestamp: DateTime.now().toISO(),
        }
        localStorage.setItem(cacheId, JSON.stringify(cacheObject));
      }
      else {
        response.timestamp = DateTime.now().toISO();
        localStorage.setItem(cacheId, JSON.stringify(response));
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next(); // Signal to cancel the request
    this.destroy$.complete();
    this.adminService.clearActiveEmpNumCache();
  }

  private handleError<T extends TimeStamped>(
    error: HttpErrorResponse,
    cachedResponse?: T | null
  ): Observable<RequestState<T>> {
    this.signalService.isLoading$.set(false);
    if (error.status === 404) {
      if (cachedResponse) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse
          )
        );
      } else {
        return of(new RequestState<T>(RequestStatus.IsCompleteNoResponse));
      }
    } else if (!window.navigator.onLine) {
      // Don't show errors if offline.
      if (cachedResponse) {
        return of(
          new RequestState<T>(
            RequestStatus.IsCompleteWithResponse,
            cachedResponse
          )
        );
      } else {
        return of(new RequestState<T>(RequestStatus.IsCompleteNoResponse));
      }
    } else {
      // Server error.
      this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: 
      Apicache Service: ${error.message}
      Status Response: ${error.status} - ${error.statusText} - ${error.error}
      URL: ${error.url}`); 
      this.errorLogService.logError(error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: `API Request Error. Status code: ${error.status}`})
      console.error(
        `Server error loading data. Status: ${error.status}. Message: `,
        error.error
      );

    }

    if (cachedResponse) {
      return of(
        new RequestState<T>(
          RequestStatus.HasErrorWithCachedResponse,
          cachedResponse
        )
      );
    } else {
      return of(new RequestState<T>(RequestStatus.HasErrorNoResponse));
    }
  }
}
